import { createApp } from '@backstage/frontend-defaults';
import { convertLegacyPlugin } from '@backstage/core-compat-api';
import apiDocsPlugin from '@backstage/plugin-api-docs/alpha';
import catalogPlugin from '@backstage/plugin-catalog/alpha';
import catalogImportPlugin from '@backstage/plugin-catalog-import/alpha';
import devtoolsPlugin from '@backstage/plugin-devtools/alpha';
import notificationsPlugin from '@backstage/plugin-notifications/alpha';
import scaffolderPlugin from '@backstage/plugin-scaffolder/alpha';
import searchPlugin from '@backstage/plugin-search/alpha';
import techdocsPlugin from '@backstage/plugin-techdocs/alpha';
import userSettingsPlugin from '@backstage/plugin-user-settings/alpha';
import bookingConfiguratorPlugin from '@internal/plugin-booking-configurator';
import hesselsGithubPlugin from '@internal/plugin-hessels-github';
import { navModule } from './modules/nav';
import catalogGraphPlugin from '@backstage/plugin-catalog-graph/alpha';
import homePlugin from '@backstage/plugin-home/alpha';
import { homeModule } from './modules/home';
import { githubPullRequestsPlugin } from '@roadiehq/backstage-plugin-github-pull-requests';
import { shortcutsPlugin } from '@backstage-community/plugin-shortcuts';
import { toolboxPlugin } from '@drodil/backstage-plugin-toolbox';
import BuildIcon from '@material-ui/icons/Build';

import {
  atlassianAuthApiRef,
  githubAuthApiRef,
} from '@backstage/core-plugin-api';
import { SignInPageBlueprint } from '@backstage/plugin-app-react';
import { SignInProviderConfig, SignInPage } from '@backstage/core-components';
import {
  configApiRef,
  createFrontendModule,
  PageBlueprint,
  RouteRef,
  useApi,
} from '@backstage/frontend-plugin-api';

const catalogGraphPage = PageBlueprint.make({
  params: {
    path: '/catalog-graph',
    routeRef: catalogGraphPlugin.routes.catalogGraph,
    loader: async () => {
      const { CatalogGraphPage } = await import(
        './components/catalogGraph/CatalogGraphPage'
      );
      return <CatalogGraphPage />;
    },
  },
});

const configuredCatalogGraphPlugin = catalogGraphPlugin.withOverrides({
  extensions: [catalogGraphPage],
});

const convertedToolboxPlugin = convertLegacyPlugin(toolboxPlugin, {
  extensions: [],
});

const toolboxPage = PageBlueprint.make({
  params: {
    path: '/toolbox',
    title: 'Toolbox',
    icon: <BuildIcon />,
    routeRef: convertedToolboxPlugin.routes.root as RouteRef,
    loader: async () => {
      const { ToolboxPage } = await import(
        './components/toolbox/ToolboxPage'
      );
      return <ToolboxPage />;
    },
  },
});

const toolboxModule = createFrontendModule({
  pluginId: 'toolbox',
  extensions: [toolboxPage],
});

const signInPage = SignInPageBlueprint.make({
  params: {
    loader: async () => props => {
      const signInProviderConfig: SignInProviderConfig[] = [
        {
          id: 'github-auth-provider',
          title: 'GitHub',
          message: 'Sign in using GitHub',
          apiRef: githubAuthApiRef,
        },
        {
          id: 'atlassian-auth-provider',
          title: 'Atlassian',
          message: 'Sign in using Atlassian',
          apiRef: atlassianAuthApiRef,
        },
      ];

      const configApi = useApi(configApiRef);
      if (configApi.getString('auth.environment') === 'development') {
        return (
          <SignInPage
            {...props}
            providers={[...signInProviderConfig, 'guest']}
          />
        );
      }

      return <SignInPage {...props} providers={signInProviderConfig} />;
    },
  },
});

export default createApp({
  features: [
    apiDocsPlugin,
    catalogPlugin,
    catalogImportPlugin,
    devtoolsPlugin,
    notificationsPlugin,
    scaffolderPlugin,
    searchPlugin,
    techdocsPlugin,
    userSettingsPlugin,
    bookingConfiguratorPlugin,
    hesselsGithubPlugin,
    configuredCatalogGraphPlugin,
    homePlugin,
    homeModule,
    convertLegacyPlugin(githubPullRequestsPlugin, { extensions: [] }),
    convertLegacyPlugin(shortcutsPlugin, { extensions: [] }),
    convertedToolboxPlugin,
    toolboxModule,
    navModule,
    createFrontendModule({
      pluginId: 'app',
      extensions: [signInPage],
    }),
  ],
});
